import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import Navigation from './components/Navigation';
import DashboardOverview from './components/DashboardOverview';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import UnifiedCallDashboard from './components/UnifiedCallDashboard';
import NextStepsReport from './components/NextStepsReport';
import CompletedCallbacks from './components/CompletedCallbacks';
import ActivityPage from './components/ActivityPage';
import CustomerDetailModal from './components/CustomerDetailModal';
import CallDetailPage from './components/CallDetailPage';
import AIAnalysis from './components/AIAnalysis';
import AICallDetail from './components/AICallDetail';
import AITestingForm from './components/AITestingForm';
import TestingResults from './components/TestingResults';
import PromptSuggestions from './components/PromptSuggestions';
import AIHub from './components/AIHub';
import { LoginPage } from './components/LoginPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext'; // New custom theme provider
import { NotificationProvider } from './components/NotificationSystem';
// import LiveStatus from './components/LiveStatus'; // Unused
import { callsApi } from './services/api';

/* OLD THEME CODE - NOW REPLACED BY ThemeContext
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { 
      main: '#2eace3', // Fongo blue
      light: '#5ec4ed',
      dark: '#1e8bb8',
      contrastText: '#ffffff'
    },
    secondary: { 
      main: '#91c83f', // Fongo green
      light: '#a8d566',
      dark: '#75a632'
    },
    success: { main: '#91c83f', light: '#a8d566', dark: '#75a632' },
    warning: { main: '#FE9339', light: '#FDD65B', dark: '#E7870B' },
    error: { main: '#ef4545', light: '#f26868', dark: '#d62e2e' },
    info: { main: '#2eace3', light: '#5ec4ed', dark: '#1e8bb8' },
    background: {
      default: '#F3F2F2', // Light gray background
      paper: '#FFFFFF'
    },
    text: {
      primary: '#080707',
      secondary: '#706E6B'
    },
    divider: '#DDDBDA'
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h1: { 
      fontSize: '2.5rem', 
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: '#080707'
    },
    h2: { 
      fontSize: '2rem', 
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: '#080707'
    },
    h3: { 
      fontSize: '1.75rem', 
      fontWeight: 600,
      color: '#080707'
    },
    h4: { 
      fontSize: '1.5rem', 
      fontWeight: 600,
      color: '#080707'
    },
    h5: { 
      fontSize: '1.25rem', 
      fontWeight: 600,
      color: '#080707'
    },
    h6: { 
      fontSize: '1rem', 
      fontWeight: 600,
      color: '#080707',
      letterSpacing: '0.025em'
    },
    body1: { 
      fontSize: '0.875rem', 
      lineHeight: 1.5,
      color: '#080707'
    },
    body2: { 
      fontSize: '0.8125rem', 
      lineHeight: 1.5,
      color: '#706E6B'
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem'
    }
  },
  shape: {
    borderRadius: 4
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 1px 3px 0 rgba(0, 0, 0, 0.10)',
          borderRadius: '4px',
          border: '1px solid #DDDBDA'
        },
        elevation1: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 1px 3px 0 rgba(0, 0, 0, 0.10)'
        },
        elevation2: {
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.07), 0 2px 6px 0 rgba(0, 0, 0, 0.12)'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #DDDBDA',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 1px 3px 0 rgba(0, 0, 0, 0.10)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          padding: '8px 16px',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        },
        contained: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          '&:hover': {
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.10)'
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #DDDBDA',
          padding: '12px 16px',
          fontSize: '0.8125rem'
        },
        head: {
          backgroundColor: '#F3F2F2',
          fontWeight: 600,
          color: '#080707',
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: '24px',
          fontSize: '0.75rem',
          fontWeight: 600
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#2eace3',
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.10)',
          borderBottom: '1px solid #1e8bb8'
        }
      }
    }
  }
});
*/

function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const handleViewChange = (view: string) => {
    setCurrentView(view);
  };

  const handleCustomerSelect = (customer: any) => {
    setSelectedCustomer(customer);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleCustomerUpdate = (updatedCustomer: any) => {
    console.log('Updated customer:', updatedCustomer);
    // TODO: Update customer in backend
    handleModalClose();
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await callsApi.getCustomers();
      console.log('Fetched customers from API:', response.data);
      
      // The API returns an array of customers directly
      const customersData = Array.isArray(response.data) ? response.data : [];
      
      // Transform API data to match customer interface
      const transformedCustomers = customersData.map((customer: any) => ({
        id: customer.customer_phone || 'unknown',
        name: customer.customer_name || 'Unknown Caller',
        phone: customer.customer_phone || 'No phone',
        email: '',
        accountNumber: null,
        issueCategory: 'General Support',
        issueDetails: 'No details available',
        urgencyLevel: 'Low' as any,
        status: 'In Progress',
        aiConfidence: 0,
        lastContact: customer.last_contact || new Date().toISOString(),
        callCount: customer.total_calls || 1,
        priority: 'Low' as any,
        startTime: customer.last_contact || undefined,
        transcript: undefined,
        recordingUrl: undefined,
        recordingPath: undefined,
        callId: undefined,
        duration: undefined,
        troubleshootingSteps: undefined,
        resolutionStatus: undefined
      }));
      
      setCustomers(transformedCustomers);
    } catch (error) {
      console.error('Error fetching customers:', error);
      // Don't fall back to sample data - show error instead
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleRefresh = () => {
    console.log('Refreshing data...');
    fetchCustomers();
  };

  const handleCustomerAction = (action: string, customer: any) => {
    console.log(`Action: ${action} for customer:`, customer);
    switch (action) {
      case 'call':
        // TODO: Initiate phone call
        break;
      case 'email':
        // TODO: Open email composer
        break;
      case 'schedule':
        // TODO: Open scheduling interface
        break;
      case 'priority':
        // TODO: Update customer priority
        break;
      case 'escalate':
        // TODO: Escalate to supervisor
        break;
      case 'edit':
        setSelectedCustomer(customer);
        setModalOpen(true);
        break;
      default:
        break;
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <DashboardOverview onViewChange={handleViewChange} />;
      case 'analytics':
        return (
          <Box sx={{ p: 3 }}>
            <AnalyticsDashboard />
          </Box>
        );
      default:
        return <DashboardOverview onViewChange={handleViewChange} />;
    }
  };

  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <AppContent
              currentView={currentView}
              onViewChange={handleViewChange}
              onRefresh={handleRefresh}
              customers={customers}
              selectedCustomer={selectedCustomer}
              modalOpen={modalOpen}
              onCustomerSelect={handleCustomerSelect}
              onCustomerAction={handleCustomerAction}
              onModalClose={handleModalClose}
              onCustomerUpdate={handleCustomerUpdate}
            />
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

// Separate component to use hooks
function AppContent({
  currentView,
  onViewChange,
  onRefresh,
  customers,
  selectedCustomer,
  modalOpen,
  onCustomerSelect,
  onCustomerAction,
  onModalClose,
  onCustomerUpdate
}: any) {
  const location = useLocation();
  const navigate = useNavigate();

  // Sync route with current view
  useEffect(() => {
    const path = location.pathname;
    if (path === '/calls' || path === '/unified-calls') {
      onViewChange('calls');
    } else if (path === '/analytics') {
      onViewChange('analytics');
    } else if (path === '/' || path === '/dashboard') {
      onViewChange('dashboard');
    }
  }, [location, onViewChange]);

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <DashboardOverview onViewChange={onViewChange} />;
      case 'analytics':
        return (
          <Box sx={{ p: 3 }}>
            <AnalyticsDashboard />
          </Box>
        );
      case 'calls':
        return <UnifiedCallDashboard />;
      default:
        return <DashboardOverview onViewChange={onViewChange} />;
    }
  };

  return (
    <>
      <Routes>
        {/* Public Route - Login */}
        <Route path="/login" element={<LoginPage onLoginSuccess={() => navigate('/dashboard')} />} />
        
        {/* Protected Routes */}
        {/* Root path redirects to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation
                currentView={currentView}
                onViewChange={onViewChange}
                onRefresh={onRefresh}
              />
              <AnalyticsDashboard />
            </Box>
          </ProtectedRoute>
        } />
        
        <Route path="/calls" element={
          <ProtectedRoute>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation 
                currentView={currentView}
                onViewChange={onViewChange}
                onRefresh={onRefresh}
              />
              <UnifiedCallDashboard />
            </Box>
          </ProtectedRoute>
        } />
        
        <Route path="/unified-calls" element={
          <ProtectedRoute>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation 
                currentView={currentView}
                onViewChange={onViewChange}
                onRefresh={onRefresh}
              />
              <UnifiedCallDashboard />
            </Box>
          </ProtectedRoute>
        } />
        
        <Route path="/calls/:callId" element={
          <ProtectedRoute>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation 
                currentView={currentView}
                onViewChange={onViewChange}
                onRefresh={onRefresh}
              />
              <CallDetailPage />
            </Box>
          </ProtectedRoute>
        } />
        
        <Route path="/next-steps" element={
          <ProtectedRoute>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation 
                currentView={currentView}
                onViewChange={onViewChange}
                onRefresh={onRefresh}
              />
              <NextStepsReport />
            </Box>
          </ProtectedRoute>
        } />
        
        <Route path="/completed-callbacks" element={
          <ProtectedRoute>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation 
                currentView={currentView}
                onViewChange={onViewChange}
                onRefresh={onRefresh}
              />
              <CompletedCallbacks />
            </Box>
          </ProtectedRoute>
        } />
        
        <Route path="/activity" element={
          <ProtectedRoute>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation 
                currentView={currentView}
                onViewChange={onViewChange}
                onRefresh={onRefresh}
              />
              <ActivityPage />
            </Box>
          </ProtectedRoute>
        } />
        
        <Route path="/analytics" element={
          <ProtectedRoute>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation 
                currentView={currentView}
                onViewChange={onViewChange}
                onRefresh={onRefresh}
              />
              <Box sx={{ p: 3 }}>
                <AnalyticsDashboard />
              </Box>
            </Box>
          </ProtectedRoute>
        } />
        
        {/* Hidden AI Analysis Routes - Direct URL Access Only */}
        <Route path="/ai-analysis" element={
          <ProtectedRoute>
            <AdminRoute>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <AIAnalysis />
              </Box>
            </AdminRoute>
          </ProtectedRoute>
        } />

        {/* AI Hub - New Unified Dashboard */}
        <Route path="/ai-hub" element={
          <ProtectedRoute>
            <AdminRoute>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <Navigation
                  currentView={currentView}
                  onViewChange={onViewChange}
                  onRefresh={onRefresh}
                />
                <AIHub />
              </Box>
            </AdminRoute>
          </ProtectedRoute>
        } />

        <Route path="/ai-analysis/call/:callId" element={
          <ProtectedRoute>
            <AdminRoute>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <AICallDetail />
              </Box>
            </AdminRoute>
          </ProtectedRoute>
        } />

        {/* Prompt Improvement Suggestions */}
        <Route path="/prompt-suggestions" element={
          <ProtectedRoute>
            <AdminRoute>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <PromptSuggestions />
              </Box>
            </AdminRoute>
          </ProtectedRoute>
        } />

        {/* AI Testing Form (Admin only) */}
        <Route path="/testing" element={<AdminRoute><AITestingForm /></AdminRoute>} />

        {/* Testing Results Dashboard (Admin only) */}
        <Route path="/testing-results" element={
          <ProtectedRoute>
            <AdminRoute>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <TestingResults />
              </Box>
            </AdminRoute>
          </ProtectedRoute>
        } />
      </Routes>
      
      <CustomerDetailModal
        open={modalOpen}
        customer={selectedCustomer}
        onClose={onModalClose}
        onUpdate={onCustomerUpdate}
      />
    </>
  );
}

export default App;
// Automatic deployment test - Wed Nov  5 16:48:33 EST 2025
